import { useContext, useEffect, useRef, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { _ROUTES } from "../../utils/_routes_constants";
import AppRights from "../../services/AppRights";
import FormTitle from "../helpers/FormTitle";
import { UserContext } from "../../contexts/UserContext";
import { PageContentLoadingIndicator } from "../ui/PageContentLoadingIndicator";
import NoDataAvailableInput from "../budget/NoDataAvailableInput";
import { BaseBudgetInputFilterSection } from "./BaseBudgetInputFilterSection";
import { useLoading } from "../../utils/useLoading";
import { DataService } from "../../services/DataService";
import { isNull } from "lodash";
import { SaveDataButton } from "./SaveDataButton";
import { _ROLES_BACK } from "../../utils/constants";
import { BaseBudgetSave } from "./BaseBudgetSave";

/**
 * @TODO: We have to remove "ColumnBuilder" from this component.
 * All columns must be managed in the TableComponent itself.
 */
export const BaseBudgetInput = ({
  title,
  roles,
  userRole,
  enableFlag,
  TableComponent,
  tableComponentProps = {},
  /**
   * @deprecated Pass the columns directly to the TableComponent
   */
  ColumnBuilder = () => [],
  filters = [],
  showFiscalDetailsOnFilterSection = true,
  hideTabs = false,
  saveData = null,
  setSaveData = null,
  /**
   * @params in BaseBudgetSave
   * arrayData: array of objects to link activity, ap and theme
   * saveNameCategory: category name to save the data
   */
  includeBaseBudgetSave = false,
  arrayData = [],
  saveNameCategory = "",
  onParamsChange = null,
  onSaveDataBefore = null,
}) => {
  const { isLoading, stop: stopLoading } = useLoading(true);
  const {
    isLoading: isTableDataLoading,
    start: startTableDataLoading,
    stop: stopTableDataLoading,
  } = useLoading(true);
  const [budgetParameters, setBudgetParameters] = useState([]);
  const [rows, setRows] = useState([]);
  const [params, setParams] = useState({});
  const [tab, setTab] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [columns] = useState(
    ColumnBuilder({ budgetParameters: budgetParameters?.[0] }),
  );
  const gridRef = useRef(null);

  // Configuration par défaut pour tous les tableaux pour le Clipboard Paste English
  const defaultTableProps = {
    agGridProps: {
      suppressClipboardPaste: false,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      ...tableComponentProps?.agGridProps,
    },
  };

  // Merger les props du tableau avec les props par défaut pour le Clipboard Paste
  const mergedTableComponentProps = {
    ...defaultTableProps,
    ...tableComponentProps,
    agGridProps: defaultTableProps.agGridProps,
  };

  const dataService = new DataService();
  const { user } = useContext(UserContext);
  const isUserAllowed = enableFlag
    ? user?.roles?.includes(userRole) && budgetParameters?.[0]?.[enableFlag]
    : user?.roles?.includes(userRole);

  const handleFilterSubmit = (values) => {
    const newParams = {
      ...params,
      ...values,
    };
    setParams(newParams);

    // Remonter les paramètres au composant parent si la fonction est fournie
    if (onParamsChange) {
      onParamsChange(newParams);
    }
  };
  // Change the tab
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };
  // Fonction pour forcer le rafraîchissement
  const handleRefreshData = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Wrapper pour setSaveData qui rafraîchit après sauvegarde
  const handleSetSaveData = (data) => {
    if (setSaveData) {
      setSaveData(data);
      // Rafraîchir après un court délai pour laisser le temps à la sauvegarde
      setTimeout(() => {
        handleRefreshData();
      }, 500);
    }
  };
  useEffect(() => {
    if (budgetParameters.length === 0) {
      startTableDataLoading();
      dataService.fetchData(
        _ROUTES.BUDGET_PARAMETERS,
        {},
        (data) => {
          setBudgetParameters(data);
          if (data && data.length > 0) {
            const newParams = {
              ...params,
              fiscalYear: data[0].dataEntryFiscalYearDate,
              version: data[0].dataEntryFiscalYearVersion,
            };
            setParams(newParams);

            // Remonter les paramètres initiaux au composant parent
            if (onParamsChange) {
              onParamsChange(newParams);
            }
          }
          stopLoading();
          stopTableDataLoading();
        },
        { errorMessage: "Unable to retrieve budget parameters" },
      );
    }
  }, [params]);

  const isSelectedAllFilters = () => {
    if (!filters.length) return true;

    return filters.every((filter) => {
      // Check for both possible formats: key and key[]
      const paramValue =
        params[filter.key] !== undefined
          ? params[filter.key]
          : params[`${filter.key}[]`];

      if (filter.allowMultiple) {
        // For multiple values, check if array exists and has items
        return Array.isArray(paramValue) && paramValue.length > 0;
      }

      // For single values, check if value is defined (including zero)
      return paramValue !== undefined && paramValue !== "";
    });
  };

  if (isLoading) {
    return <PageContentLoadingIndicator />;
  }

  if (budgetParameters.length === 0) {
    return <NoDataAvailableInput />;
  }

  return (
    <>
      <AppRights roles={[...roles, isUserAllowed ? userRole : null]} />
      <FormTitle title={title} />
      {!hideTabs && (
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="Input" />
          {includeBaseBudgetSave &&
            (roles.includes(_ROLES_BACK._ROLE_ADMIN) ||
              roles.includes(_ROLES_BACK._ROLE_CTRL)) && (
              <Tab label="Parameters" />
            )}
        </Tabs>
      )}
      {tab === 0 && (
        <>
          <Box
            width={"100%"}
            p={2}
            sx={{
              backgroundColor: "white",
            }}
          >
            <BaseBudgetInputFilterSection
              filters={filters}
              budgetParameters={budgetParameters}
              onFilterSubmit={handleFilterSubmit}
              showFiscalDetails={showFiscalDetailsOnFilterSection}
            />
            <Box
              sx={{
                display: "flex",
                marginTop: 2,
                gap: 1,
              }}
            >
              {budgetParameters.length > 0 && !filters.length && (
                <Box
                  sx={{
                    width: "15%",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginRight: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    component="div"
                    fontWeight={"bold"}
                  >
                    {budgetParameters?.[0]?.dataEntryFiscalYearDate || "N/A"}
                    {" - "}
                    {`Version ${budgetParameters?.[0]?.dataEntryFiscalYearVersion}`}
                  </Typography>
                </Box>
              )}
            </Box>
            {isSelectedAllFilters() && (
              <Box style={{ height: 500 }}>
                <TableComponent
                  key={refreshTrigger}
                  gridRef={gridRef}
                  rows={rows}
                  columns={columns}
                  setRows={setRows}
                  params={params}
                  isDataLoading={isTableDataLoading}
                  onDataLoadingStart={startTableDataLoading}
                  onDataLoadingStop={stopTableDataLoading}
                  onRefreshData={handleRefreshData}
                  {...mergedTableComponentProps}
                  saveData={saveData}
                  setSaveData={handleSetSaveData}
                />
                <Box sx={{ height: "2cm" }} />
              </Box>
            )}
          </Box>
          {!isNull(saveData) && (
            <SaveDataButton
              gridRef={gridRef}
              setSaveData={handleSetSaveData}
              saveData={saveData}
              onSaveDataBefore={onSaveDataBefore}
            />
          )}
        </>
      )}
      {tab === 1 && !hideTabs && (
        <BaseBudgetSave arrayData={arrayData} category={saveNameCategory} />
      )}
    </>
  );
};
